// The Master Permission Matrix (CR-065 "Master Permission Matrix"; B10 plan D14, slice S20b; DC-217 "The Permission
// matrix is a read-only table in his exact shape"). One view over `permissions.matrix`, which reads
// `app/permissions/matrix.yaml` — the file every authorization decision is taken against — verbatim: the eight role
// columns in the matrix's order (DC-228 as amended 12 Oct 05:29), every capability row in his words, each cell his
// access word. It is drawn in two places: the page `#settings/permissions` (the Admin, whose *Audit ledger* cell is
// "Admin") and the *Permissions engine* section of System Admin. Nothing on it changes the matrix: a change is a code
// change with a regenerated authorization matrix ("SSO, permissions engine, audit engine, credentials remain
// system-admin controlled.").

import { Card, EmptyState, G6_LABELS, PageHeader, PermissionMatrix, Stack, type AccessWord, type PermissionRow, type SeatKey } from "@/mc";
import { permissionsMatrix, type PermissionsMatrixOutput } from "@/api/tools";
import { errorText, useRead } from "@/api/useTool";
import type { AppError } from "@/api/runtime";
import { V1Slot } from "@/shell/island";
import { NotFound } from "@/views/NotFound";
import type { Route } from "@/router";
import { SYSTEM_LABELS } from "./labels";

/** matrix.yaml's role keys (the backend's MATRIX_ROLES) → D0's seat keys, in the matrix's order (the BRD's order with the
 *  dropped columns removed and the Admin last, cockpit ruling 12 Oct 06:10). */
export const ROLE_SEAT: Readonly<Record<string, SeatKey>> = {
  hm: "hm",
  recruiter: "recruiter",
  coordinator: "coordinator",
  hrbp: "hrbp",
  compensation: "compensation",
  interviewer: "interviewer",
  ta_manager: "taManager",
  admin: "admin",
};

/** His cell word as the matrix draws it: verbatim. Since DC-228 the bare *Admin* is his word for the Admin column's
 * cells and the column head, so no cell is re-worded. */
export function cellWord(word: string): AccessWord {
  return word as AccessWord;
}

/** The matrix as D0's PermissionMatrix rows: the file's row order, his capability words, every role's cell. A role
 * the file names but the seat map does not is left out rather than drawn under another seat. */
export function matrixRows(matrix: Pick<PermissionsMatrixOutput, "rows">): PermissionRow[] {
  return matrix.rows.map((row) => {
    const cells: Partial<Record<SeatKey, AccessWord>> = {};
    for (const [role, word] of Object.entries(row.cells)) {
      const seat = ROLE_SEAT[role];
      if (seat !== undefined) cells[seat] = cellWord(word);
    }
    return { capability: row.label, cells };
  });
}

/** A short form of the file's sha256, the version every decision was taken against (shown in mono). */
export function shortVersion(version: string): string {
  return version.slice(0, 12);
}

export interface PermissionMatrixViewProps {
  matrix?: PermissionsMatrixOutput | undefined;
  loading?: boolean | undefined;
  error?: AppError | null | undefined;
  onRetry?: (() => void) | undefined;
  /** The scroll height of the matrix card in px. */
  scroll?: number | undefined;
}

/** The matrix card: data in, markup out (no reads), so the System Admin section and the page share it. */
export function PermissionMatrixView({ matrix, loading, error, onRetry, scroll = 560 }: PermissionMatrixViewProps) {
  if (error) {
    return <Card title={G6_LABELS.permissionMatrix} error={{ message: errorText(error), onRetry, details: { correlationId: error.requestId ?? undefined, status: error.status } }} />;
  }
  if (loading || !matrix) return <Card title={G6_LABELS.permissionMatrix} loading={{ lines: 8 }} />;
  const rows = matrixRows(matrix);
  return (
    <Card
      title={G6_LABELS.permissionMatrix}
      sub={<span className="mc-mono" title={matrix.version}>{shortVersion(matrix.version)}</span>}
      flush
      
    >
      {rows.length === 0 ? <EmptyState inline>—</EmptyState> : <PermissionMatrix rows={rows} scroll={scroll} />}
    </Card>
  );
}

/** One read of the matrix, shared by the page and the System Admin section (one cache key). */
export function useMatrix() {
  return useRead(["cfg", "permissions.matrix"], () => permissionsMatrix({}), { staleTime: 5 * 60_000 });
}

/** `#settings/permissions`: the matrix on its own page. Gated on `permissions.matrix` by its registration. */
export function PermissionMatrixPage({ route }: { route: Route }) {
  const q = useMatrix();
  // the server's refusal reads as the not-found screen (N02), never as the matrix's error strip
  if (q.error && (q.error.status === 403 || q.error.status === 404)) return <V1Slot><NotFound route={route.raw} /></V1Slot>;
  return (
    <Stack>
      <PageHeader
        title={G6_LABELS.permissionMatrix}
        meta={[SYSTEM_LABELS.systemAdminControlled]}
        loading={q.isLoading}
      />
      <PermissionMatrixView matrix={q.data} loading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} />
    </Stack>
  );
}
